/**
 * Author: Codex CLI 助手
 * Generation: Global loading integration + color mapping fix
 * Version: v3.2.169
 * Modified: 2025-10-25
 * GamePage - 全屏游戏界面（Phaser3全屏 + UI漂浮叠加）
 */

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LiveChatPanel } from '../components/overlay/LiveChatPanel';
import { useAuthStore } from '@store/AuthStore';
import { PlayerLeaderboard } from '../components/overlay/PlayerLeaderboard';
import { BettingLeaderboard } from '../components/overlay/BettingLeaderboard';
import { AgentPanel } from '../components/overlay/AgentPanel';
import { AgentData } from '../components/overlay/AgentCard';
import { ActivityLogBar, ActivityLog } from '../components/overlay/ActivityLogBar';
import { TopBar } from '../components/overlay/TopBar';
import WinnerBanner from '../components/WinnerBanner';
import { getGameManager } from '@game/GameManager';
import webSocketService from '../services/websocket/WebSocketService';
import { usePortraitPreloader } from '../hooks/usePortraitPreloader';
import { colorToIndex, normalizeColor, indexToEmoji } from '../utils/colorMapping';
import { useRoomStore, RoomParticipant } from '@store/RoomStore';
import { FrontendTestLogger, FrontendEventCategory } from '@utils/TestLogger';
import '../styles/overlay-ui.css';

interface ScenarioInfo { name: string; description?: string }

const DEFAULT_FAKE_SCENARIOS: ScenarioInfo[] = [
  { name: 'move_then_collect', description: 'Move multiple steps then collect resource' },
  { name: 'move_into_combat', description: 'Movement followed by combat' },
  { name: 'combat_then_move', description: 'Combat then winner moves' },
  { name: 'combat_vs_safezone', description: 'Combat while another dies to safezone' },
  { name: 'move_then_use_item', description: 'Movement then use item (if backend supports)' },
  { name: 'long_combat_with_death', description: 'Long combat chain with elimination' }
];

// v3.2.139: Map RoomParticipant to AgentData for UI display
const mapParticipantsToAgentData = (participants: RoomParticipant[]): AgentData[] => {
  return participants.map((p, index) => {
    // Resolve color index preference: characterType (number) → color (name) → slimeType (emoji) → default(0)
    let idx = 0;
    const conf: any = p.agentConfiguration || {};
    if (typeof conf.characterType === 'number') {
      idx = Math.max(0, Math.min(6, conf.characterType | 0));
    } else if (conf.color) {
      idx = colorToIndex(conf.color);
    } else if (conf.slimeType) {
      idx = colorToIndex(conf.slimeType);
    }
    return {
      id: index + 1,
      name: conf?.name || p.username,
      status: p.isReady ? 'active' : 'waiting',
      hp: conf?.hp || 100,
      maxHp: 100,
      weapon: 'Unknown',
      inventory: [],
      lastThinking: p.isReady ? 'Ready' : 'Configuring...',
      character: indexToEmoji(idx),
      colorIndex: idx
    } as AgentData;
  });
};

// Mock Activity Log数据
const mockActivityLogs: ActivityLog[] = [
  {
    id: 1,
    timestamp: '15:23:45',
    agentName: 'Agent Alpha',
    character: '⚔️',
    type: 'thinking',
    content: 'Analyzing safe zone position... Moving towards zone center to avoid storm damage'
  },
  {
    id: 2,
    timestamp: '15:23:48',
    agentName: 'Agent Beta',
    character: '🏹',
    type: 'move',
    content: 'Moving to vantage point at (180, 220) - high ground advantage detected'
  },
  {
    id: 3,
    timestamp: '15:23:52',
    agentName: 'Agent Delta',
    character: '🗡️',
    type: 'combat',
    content: 'Engaged Agent Gamma in close-range combat - using shotgun tactical advantage'
  },
  {
    id: 4,
    timestamp: '15:23:55',
    agentName: 'Agent Alpha',
    character: '⚔️',
    type: 'move',
    content: 'Successfully reached position (245, 180) - scanning for nearby resources'
  },
  {
    id: 5,
    timestamp: '15:23:58',
    agentName: 'Agent Echo',
    character: '🛡️',
    type: 'thinking',
    content: 'Waiting in defensive position... Monitoring enemy movements before advancing'
  },
  {
    id: 6,
    timestamp: '15:24:02',
    agentName: 'Agent Beta',
    character: '🏹',
    type: 'item',
    content: 'Collected Sniper Scope - increased accuracy range by 40%'
  },
  {
    id: 7,
    timestamp: '15:24:05',
    agentName: 'Agent Gamma',
    character: '🔮',
    type: 'combat',
    content: 'Eliminated by Agent Delta - final position (195, 165)'
  },
  {
    id: 8,
    timestamp: '15:24:09',
    agentName: 'Agent Delta',
    character: '🗡️',
    type: 'thinking',
    content: 'Victory in combat! Searching for healing items to recover HP...'
  }
];

export const GamePage: React.FC = () => {
  const gameCanvasRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { roomId: routeRoomId } = useParams();
  const { isAuthenticated } = useAuthStore();

  // 控制3个面板的展开/收起状态
  const [showChat, setShowChat] = useState(false);
  const [showPlayerLeaderboard, setShowPlayerLeaderboard] = useState(false);
  const [showBettingLeaderboard, setShowBettingLeaderboard] = useState(false);
  const [autoFollow, setAutoFollow] = useState<boolean>(true);

  // v3.2.139: Use RoomStore for real participant data
  const { currentRoom } = useRoomStore();
  const participants = currentRoom?.participants ?? [];
  // 面板Agent数据（初始化自 RoomStore，随后由快照/事件实时维护）
  const [panelAgents, setPanelAgents] = useState<AgentData[]>([]);
  // 注意：不要把 participants 数组对象本身放入依赖（引用每次 render 可能不同，导致无限触发）
  useEffect(() => {
    if (!currentRoom) { setPanelAgents([]); return; }
    const len = participants.length;
    if (len === 0) { setPanelAgents([]); return; }
    setPanelAgents(mapParticipantsToAgentData(participants));
  }, [currentRoom?.id, participants.length]);

  // Agent数据和用户余额
  const [userBalance, setUserBalance] = useState(5420);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>(mockActivityLogs);
  const [currentRound, setCurrentRound] = useState(1);
  const [mapReady, setMapReady] = useState(false);
  const scenarioPanelEnabled = useMemo(() => {
    if ((import.meta as any).env?.DEV) return true;
    if (typeof window !== 'undefined' && (window as any).__ABR_SCENARIO_PANEL__ === true) return true;
    return false;
  }, []);
  const [scenarioList, setScenarioList] = useState<ScenarioInfo[]>([]);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [activeScenario, setActiveScenario] = useState<string | null>(null);
  const [scenarioStatus, setScenarioStatus] = useState<string>('idle');

  // WebSocket连接状态
  const [connected, setConnected] = useState(false);
  const [socketUrl] = useState<string>(() => {
    try { return webSocketService.getConfig().url; } catch { return 'http://localhost:8080'; }
  });
  const scenarioChoices = scenarioList.length ? scenarioList : DEFAULT_FAKE_SCENARIOS;
  const scenarioRunningLabel = activeScenario ? `${activeScenario} (${scenarioStatus})` : 'Idle';
  const scenarioButtonsDisabled = !connected || !!activeScenario;

  // Portrait preloader for agent cards
  const { portraits, loading: portraitsLoading } = usePortraitPreloader();

  // 刷新恢复：不再使用 last-room-id 回退；仅接受路由参数或已知 currentRoom
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { localStorage.setItem('ABR_TRACE_AUTOEXPORT','0'); } catch {}
      if (!isAuthenticated) {
        console.warn('[Guard] Not authenticated; redirecting to /lobby');
        navigate('/lobby');
        return;
      }
      if (!currentRoom) {
        const useId = (routeRoomId as string) || undefined;
        if (useId) {
          try {
            const { joinRoomAsSpectator } = useRoomStore.getState();
            const ok = await joinRoomAsSpectator(useId);
            if (ok) {
              try { webSocketService.sendSceneReady(useId, 'BattleScene'); } catch {}
              try { await webSocketService.requestSnapshot(useId); } catch {}
              return; // 停留在当前页面，等待事件流恢复
            }
          } catch {}
        }
        // 等待3秒给其它恢复路径机会，失败则回 Lobby
        setTimeout(() => {
          if (!cancelled && !useRoomStore.getState().currentRoom) {
            console.warn('[Guard] No active room after retry; redirecting to /lobby');
            navigate('/lobby');
          }
        }, 3000);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, currentRoom?.id, navigate]);

  // Dev scenario helper: when visiting /game/room_dev_one_turn, auto-connect WS and send scene:ready once
  useEffect(() => {
    (async () => {
      const rid = routeRoomId || '';
      if (rid !== 'room_dev_one_turn') return;
      try {
        if (typeof window !== 'undefined' && (window as any).__ABR_DEV_SCN_READY__) return;
        const ws = (await import('../services/websocket/WebSocketService')).default;
        // Prefer explicit override URL if provided via window.__ABR_WS_URL__
        const targetUrl = (typeof window !== 'undefined' && (window as any).__ABR_WS_URL__) || undefined;
        await ws.connect(targetUrl);
        (window as any).__ABR_DEV_SCN_READY__ = true;
        try { (await import('../services/telemetry/ClientTrace')).default.log('ws','ws-bridge','DEV_SCENARIO_CONNECT', { details: { url: targetUrl || ws.getConfig().url } }); } catch {}
        ws.sendSceneReady('room_dev_one_turn','BattleScene');
      } catch {}
    })();
  }, [routeRoomId]);

  // 处理下注
  const handleBet = (agentId: number, amount: number) => {
    if (amount <= userBalance) {
      setUserBalance(prev => prev - amount);
      console.log(`Bet ${amount} on Agent ${agentId}`);
      // 这里将来会发送到后端
    }
  };

  // Initialize Phaser Game Engine (不自动连接WebSocket)
  useEffect(() => {
    let mounted = true;

    // Enable game mode (disable body scroll)
    document.body.classList.add('game-mode');

    (async () => {
      try {
        console.log('🎮 Initializing Phaser Game Engine...');
        try {
          const CT = (await import('../services/telemetry/ClientTrace')).default;
          CT.log('load','gamepage','INIT');
          // 默认安装退出保留+下次进入合并导出（不进行周期性下载）
          CT.installAutoHooks();
        } catch {}

        const gm = getGameManager();

        // Ensure roomId is propagated to BattleScene before scenes start
        const initialRoomId = routeRoomId || useRoomStore.getState().currentRoom?.id || undefined;
        if (initialRoomId) {
          gm.setCurrentRoomId(initialRoomId);
          try { (window as any).__ABR_CURRENT_ROOM_ID__ = initialRoomId; } catch {}
          try { (await import('../services/telemetry/ClientTrace')).default.log('load','gamepage','ROOM_ID_SET',{ roomId: initialRoomId }); } catch {}
        }

        // Initialize without WebSocket connection
        await (gm as any).initialize?.({ connect: false });

        if (mounted) {
          console.log('🚀 Starting Phaser game...');
          gm.start();
          console.log('⏳ Waiting for BattleScene to auto-generate map...');
          try { (await import('../services/telemetry/ClientTrace')).default.log('load','gamepage','PHASER_STARTED'); } catch {}

          // 配置 PlaybackScheduler（状态先行 + 串行可视化回放）
          try {
            const ws = (await import('../services/websocket/WebSocketService')).default;
            const waitForCombatRouterReady = () => {
              if (typeof window === 'undefined') { return Promise.resolve(); }
              return new Promise<void>((resolve) => {
                const finish = () => { Promise.resolve().then(() => resolve()); };
                if ((window as any).__ABR_CR_BOUND) {
                  finish();
                  return;
                }
                let done = false;
                let timer: number | null = null;
                const cleanup = () => {
                  if (timer !== null) {
                    window.clearTimeout(timer);
                    timer = null;
                  }
                  try { window.removeEventListener('ABR_COMBAT_ROUTER_READY' as any, handler as any); } catch {}
                };
                const handler = () => {
                  if (done) return;
                  done = true;
                  cleanup();
                  finish();
                };
                timer = window.setTimeout(() => {
                  if (done) return;
                  done = true;
                  cleanup();
                  resolve();
                }, 4000);
                window.addEventListener('ABR_COMBAT_ROUTER_READY' as any, handler as any);
              });
            };
            const waitFor = (eventName: string, match: (detail: any)=>boolean, timeoutMs = 8000) => new Promise<void>((resolve) => {
              let done = false;
              const handler = (ev: any) => {
                try {
                  const d = ev?.detail ?? {};
                  if (match(d)) {
                    done = true;
                    window.removeEventListener(eventName as any, handler as any);
                    resolve();
                  }
                } catch {}
              };
              window.addEventListener(eventName as any, handler as any);
              setTimeout(() => {
                if (!done) {
                  try { window.removeEventListener(eventName as any, handler as any); } catch {}
                  resolve();
                }
              }, timeoutMs);
            });
            // 状态优先辅助（仅用于“是否跳过等待”，事件一律注入）
            const { shouldSkipMoveWait, alreadyEliminated } = await import('../services/playback/Waiters');
            try { (await import('../services/telemetry/ClientTrace')).default.log('playback','gamepage','PLAYBACK_ENABLED'); } catch {}
            ws.configurePlayback({
              onTurnStart: async (agentId: string) => {
                ws.triggerGameEvent('AGENT_TURN_STARTED', { agentId, __viz_internal: true });
              },
              playMove: async (agentId: string, path: Array<{x:number;y:number}>) => {
                const p = Array.isArray(path) ? path : [];
                // 零步：短等待，固定时长由 WebSocketService 固定 gate 负责
                if (!p || p.length <= 1) { await new Promise<void>(r => setTimeout(r, 80)); return; }
                const target = p[p.length - 1];
                ws.triggerGameEvent('AGENT_MOVED', { agentId, path: p, toPosition: target, __viz_internal: true });
                // 不再等待 ABR_ALL_MOVES_DONE；固定时长 gate 将控制队列推进
              },
              onCombatStart: async (data?: any) => {
                ws.triggerGameEvent('COMBAT_STARTED', { ...(data||{}), __viz_internal: true });
                // 黑箱开始信号：不阻塞回放，由 ABR_COMBAT_ENDED 统一收口
              },
              playAttack: async (attackerId: string, defenderId: string, damage?: number, data?: any) => {
                // 透传完整payload以保证 combatId/__seq/isCritical 不丢失
                ws.triggerGameEvent('COMBAT_DAMAGE_DEALT', { ...(data||{}), attackerId, defenderId, damage, __viz_internal: true });
                // 战斗分段不等待，由 ABR_COMBAT_ENDED 统一收口
              },
              onCombatMiss: async (data?: any) => {
                ws.triggerGameEvent('COMBAT_ATTACK_MISSED', { ...(data||{}), __viz_internal: true });
                // 战斗分段不等待，由 ABR_COMBAT_ENDED 统一收口
              },
              onCombatEnd: async (data?: any) => {
                ws.triggerGameEvent('COMBAT_ENDED', { ...(data||{}), __viz_internal: true });
              },
              playHpChange: async (agentId: string, delta: number, current?: number, max?: number) => {
                ws.triggerGameEvent('AGENT_HEALTH_CHANGED', { agentId, changeAmount: delta, current, max, __viz_internal: true });
                await new Promise<void>(r => setTimeout(r, 180));
              },
              playElimination: async (agentId: string, cause?: string) => {
                const since = Date.now();
                if (alreadyEliminated(agentId, since)) return;
                const waiter = waitFor('ABR_ELIMINATION_DONE', (d) => d?.agentId === agentId, 8000);
                ws.triggerGameEvent('AGENT_ELIMINATED', { agentId, cause, __viz_internal: true });
                await waiter;
              },
              showResourceMarker: async (data?: any) => {
                ws.triggerGameEvent('RESOURCE_SPAWNED', { ...(data||{}), __viz_internal: true });
                await new Promise<void>(r => setTimeout(r, 120));
              },
              onResourceCollect: async (data?: any) => {
                const type = String((data?.type || data?.item?.type || '')).toUpperCase();
                const isWeapon = (type === 'WEAPON');
                const isSystem = (data?.agentId === 'system' || !data?.agentId);
                if (isSystem) {
                  // 系统清扫/过期移除：不进入等待，短 marker 即过
                  ws.triggerGameEvent('RESOURCE_COLLECTED', { ...(data||{}), __viz_internal: true });
                  await new Promise<void>(r => setTimeout(r, 80));
                } else if (isWeapon) {
                  // 武器：不等收集DONE，等待转移到装备事件
                  ws.triggerGameEvent('RESOURCE_COLLECTED', { ...(data||{}), __viz_internal: true });
                  await new Promise<void>(r => setTimeout(r, 100));
                } else {
                  const waiter = waitFor('ABR_RESOURCE_COLLECT_DONE', (d) => {
                    if (!data) return true;
                    const itemOk = !data.itemId || d?.itemId === data.itemId;
                    // 放宽 agentId 匹配，或允许 system 视为通用收口
                    const aidOk = !data.agentId || d?.agentId === data.agentId || d?.agentId === 'system';
                    return aidOk && itemOk;
                  }, 8000);
                  ws.triggerGameEvent('RESOURCE_COLLECTED', { ...(data||{}), __viz_internal: true });
                  await waiter;
                }
              },
              showSafezoneMarker: async (type?: string, data?: any) => {
                const evt = type || 'SAFEZONE_UPDATED';
                ws.triggerGameEvent(evt, { ...(data||{}), __viz_internal: true });
                await new Promise<void>(r => setTimeout(r, 120));
              },
              onWeaponEquip: async (data?: any) => {
                // WEAPON_EQUIPPED：等待真实挥击结束信号，确保镜头/箭头不提前切换
                const agentId = String((data as any)?.agentId || (data as any)?.id || '');
                const weaponType = (data as any)?.weaponType || (data as any)?.weapon?.type;
                const waiter = waitFor(
                  'ABR_WEAPON_EQUIP_DONE',
                  (d) => d?.agentId === agentId,
                  2000
                );
                ws.triggerGameEvent('WEAPON_EQUIPPED', { ...(data||{}), __viz_internal: true });
                await waiter; // 若2s内未到达则兜底放行（记录WAIT_TIMEOUT）
              },
              onTurnEnd: async (agentId?: string) => {
                ws.triggerGameEvent('AGENT_TURN_ENDED', { agentId, __viz_internal: true });
              }
            }, { watchdogMs: 30000 });
            await waitForCombatRouterReady();
          } catch (e) {
            console.warn('PlaybackScheduler configure failed:', (e as Error).message);
          }

          // Test logging: Track GamePage rendering
          try {
            const ClientTrace = (await import('../services/telemetry/ClientTrace')).default;
            ClientTrace.log('render','gamepage','GAMEPAGE_RENDERED', { roomId: useRoomStore.getState().currentRoom?.id, details: { hasGameManager: !!gm } });
          } catch {}
        }
      } catch (e) {
        console.error('❌ Phaser initialization failed:', e);
      }
    })();

    // Listen for map ready event from Phaser scene
    const onMapReady = () => {
      console.log('✅ Map ready! Resources and terrain loaded.');
      setMapReady(true);
    };

    window.addEventListener('ABR_MAP_READY', onMapReady);

    return () => {
      mounted = false;
      console.log('🔄 Cleaning up Phaser game...');

      // Disable game mode (restore body scroll)
      document.body.classList.remove('game-mode');

      try {
        const gm = getGameManager();
        gm.destroy();
      } catch (e) {
        console.error('Cleanup error:', e);
      }

      window.removeEventListener('ABR_MAP_READY', onMapReady);
    };
  }, []);

  // 监听权威快照/地图生成：初始化面板Agent的 hp/maxHp（颜色使用 a.color/characterType）
  useEffect(() => {
    const onSnapshot = (snap?: any) => {
      setMapReady(true);
      try {
        const agents = Array.isArray(snap?.agents) ? snap.agents : [];
        // 初始化回合号
        try {
          const turnFromSnap = Number(snap?.turn?.current);
          if (!Number.isNaN(turnFromSnap) && turnFromSnap > 0) setCurrentRound(turnFromSnap);
        } catch {}
        if (agents.length) {
          // 将 snapshot 的健康状态映射到面板
          setPanelAgents(prev => {
            const mapped: AgentData[] = agents.map((a:any, idx:number) => {
              let colorIndex = 0;
              if (typeof a?.characterType === 'number') colorIndex = Math.max(0, Math.min(6, a.characterType|0));
              else colorIndex = colorToIndex(a?.color || a?.slimeType);
              return {
                id: idx + 1,
                agentId: a?.agentId || a?.id || `agent_${idx+1}`,
                name: a?.name || (a?.agentId || `Agent ${idx+1}`),
                status: a?.status === 'dead' ? 'died' : 'active',
                hp: a?.health?.current ?? a?.health ?? a?.currentHealth ?? 100,
                maxHp: a?.health?.max ?? a?.maxHealth ?? 100,
                weapon: (a?.weapon?.type || a?.equippedWeapon || 'Unknown') as string,
                // 初始化背包仅保留医疗，渲染时再聚合计数
                inventory: Array.isArray(a?.inventory)
                  ? a.inventory
                      .filter((it: any) => ['medical','consumable'].includes(String(it?.category||'').toLowerCase()))
                      .map((it: any) => (String(it?.medicalType||'').toLowerCase()==='small' ? 'small-medkit' : 'large-medkit'))
                      .slice(0, 8)
                  : [],
                lastThinking: '-',
                character: indexToEmoji(colorIndex),
                colorIndex
              } as AgentData;
            });
            return mapped;
          });
        }
      } catch {}
    };
    // 绑定与解绑需使用同一函数引用
    const onMapGenerated = () => setMapReady(true);
    try {
      webSocketService.on('SYSTEM_STATE_SYNC', onSnapshot);
      webSocketService.on('MAP_GENERATED', onMapGenerated);
    } catch {}
    return () => {
      try {
        webSocketService.off('SYSTEM_STATE_SYNC', onSnapshot);
        webSocketService.off('MAP_GENERATED', onMapGenerated);
      } catch {}
    };
  }, []);

  // v3.2.183: 回合事件绑定（TURN_STARTED）
  useEffect(() => {
    const onTurnStarted = (d: any) => {
      const tn = Number(d?.turnNumber ?? d?.turn);
      if (!Number.isNaN(tn) && tn > 0) setCurrentRound(tn);
    };
    try { webSocketService.on('TURN_STARTED', onTurnStarted); } catch {}
    return () => { try { webSocketService.off('TURN_STARTED', onTurnStarted); } catch {} };
  }, []);

  // v3.2.183: 快照兜底（观战进入后可能错过首个快照）
  useEffect(() => {
    const roomId = (routeRoomId as string) || useRoomStore.getState().currentRoom?.id;
    if (roomId) {
      try { webSocketService.requestSnapshot(roomId); } catch {}
    }
  }, [currentRoom?.id, routeRoomId]);

  // 实时同步新出生Agent的面板颜色（AGENT_SPAWNED）
  useEffect(() => {
    const onSpawned = (p: any) => {
      try {
        const id = String(p?.agent?.id || p?.agentId || '');
        let colorIndex = 0;
        if (typeof p?.agent?.characterType === 'number') colorIndex = Math.max(0, Math.min(6, p.agent.characterType|0));
        else colorIndex = colorToIndex(p?.agent?.color || p?.color || p?.slimeType);
        setPanelAgents(prev => {
          const exists = prev.find(a => a.agentId === id || a.name === id);
          if (exists) {
            return prev.map(a => (a.agentId === id || a.name === id)
              ? { ...a, character: indexToEmoji(colorIndex), colorIndex }
              : a);
          }
          // Append new entry if not present (ensure immediate correct color)
          const name = String(p?.agent?.name || id);
          const hp = p?.agent?.health?.current ?? p?.agent?.health ?? 100;
          const maxHp = p?.agent?.health?.max ?? 100;
          const appended: AgentData = {
            id: prev.length + 1,
            agentId: id,
            name,
            status: 'active',
            hp,
            maxHp,
            weapon: 'Unknown',
            inventory: [],
            lastThinking: '-',
            character: indexToEmoji(colorIndex),
            colorIndex
          };
          return [...prev, appended];
        });
      } catch {}
    };
    try { webSocketService.on('AGENT_SPAWNED', onSpawned); } catch {}
    return () => { try { webSocketService.off('AGENT_SPAWNED', onSpawned); } catch {} };
  }, []);

  // 订阅武器/物品/LLM事件，更新面板
  useEffect(() => {
    const onEquipped = (d:any) => {
      try {
        const agentId = String(d?.agentId||'');
        const weaponType = String(d?.weaponType || d?.weapon?.weaponType || d?.weapon?.type || '').toUpperCase() || 'SWORD';
        setPanelAgents(prev => prev.map(a => (a.agentId === agentId) ? { ...a, weapon: weaponType } : a));
      } catch {}
    };
    const onBroken = (d:any) => {
      try {
        const agentId = String(d?.agentId||'');
        setPanelAgents(prev => prev.map(a => (a.agentId === agentId) ? { ...a, weapon: 'None' } : a));
      } catch {}
    };
    const onCollected = (d:any) => {
      try {
        const agentId = String(d?.agentId||'');
        if (agentId === 'system') return; // 系统清退不计入背包
        const cat = String(d?.item?.category || d?.category || '').toLowerCase();
        if (cat === 'medical') {
          const badge = (d?.medicalType === 'small' || d?.item?.type === 'small') ? 'small-medkit' : 'large-medkit';
          setPanelAgents(prev => prev.map(a => (a.agentId === agentId)
            ? { ...a, inventory: a.inventory.length < 8 ? [...a.inventory, badge] : a.inventory }
            : a));
        }
      } catch {}
    };
    const onConsumed = (d:any) => {
      try {
        const agentId = String(d?.agentId||'');
        const name = String(d?.item?.name || d?.itemType || '').toLowerCase();
        const badge = name.includes('small') ? 'small-medkit' : (name.includes('large') ? 'large-medkit' : name);
        setPanelAgents(prev => prev.map(a => (a.agentId === agentId)
          ? { ...a, inventory: a.inventory.filter(x => x !== badge) }
          : a));
      } catch {}
    };
    const onLLM = (d:any) => {
      try {
        const agentId = String(d?.agentId || d?.agent?.id || '');
        const tl = d?.TL?.objective || d?.tl?.objective || d?.summary || d?.HL?.text || '';
        if (!agentId) return;
        setPanelAgents(prev => prev.map(a => (a.agentId === agentId) ? { ...a, lastThinking: String(tl||'-') } : a));
      } catch {}
    };
    try {
      webSocketService.on('WEAPON_EQUIPPED', onEquipped);
      webSocketService.on('WEAPON_BROKEN', onBroken);
      webSocketService.on('RESOURCE_COLLECTED', onCollected);
      webSocketService.on('ITEM_CONSUMED', onConsumed);
      webSocketService.on('LLM_STRATEGY_ANALYZED', onLLM);
    } catch {}
    return () => {
      try {
        webSocketService.off('WEAPON_EQUIPPED', onEquipped);
        webSocketService.off('WEAPON_BROKEN', onBroken);
        webSocketService.off('RESOURCE_COLLECTED', onCollected);
        webSocketService.off('ITEM_CONSUMED', onConsumed);
        webSocketService.off('LLM_STRATEGY_ANALYZED', onLLM);
      } catch {}
    };
  }, []);

  // 记录最近加入房间以支持刷新后恢复
  useEffect(() => {
    if (currentRoom?.id) {
      // 禁止写入 last-room-id（仅使用显式路由传递 roomId）
    }
  }, [currentRoom?.id]);

  // 实时同步 HP/死亡到面板
  useEffect(() => {
  const onHp = (d:any) => {
      try {
        const agentId = String(d?.agentId || '');
        setPanelAgents(prev => prev.map(a => (a.agentId === agentId)
          ? { ...a, hp: d?.currentHealth?.current ?? a.hp, maxHp: d?.currentHealth?.max ?? a.maxHp }
          : a
        ));
      } catch {}
    };
    const onElim = (d:any) => {
      try {
        const agentId = String(d?.agentId || '');
        setPanelAgents(prev => prev.map(a => (a.agentId === agentId) ? { ...a, status: 'died', hp: 0 } : a));
      } catch {}
    };
    try {
      webSocketService.on('AGENT_HEALTH_CHANGED', onHp);
      webSocketService.on('AGENT_ELIMINATED', onElim);
    } catch {}
    return () => {
      try {
        webSocketService.off('AGENT_HEALTH_CHANGED', onHp);
        webSocketService.off('AGENT_ELIMINATED', onElim);
      } catch {}
    };
  }, []);

  // Keep roomId in sync after initialization
  useEffect(() => {
    const gm = getGameManager();
    const rid = (routeRoomId as string) || currentRoom?.id || undefined;
    if (rid) {
      gm.setCurrentRoomId(rid);
    }
  }, [currentRoom?.id, routeRoomId]);

  // 监听连接状态变化
  useEffect(() => {
    const id = setInterval(() => {
      try {
        setConnected(webSocketService.isConnected());
      } catch {}
    }, 500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!scenarioPanelEnabled) return;
    if (!connected) return;
    refreshScenarioList();
  }, [connected, scenarioPanelEnabled]);

  // 手动连接WebSocket
  const connect = async () => {
    try {
      await webSocketService.connect(socketUrl);
      setConnected(true);
      console.log(`✅ Connected to ${socketUrl}`);
    } catch (e: any) {
      setConnected(false);
      console.error(`❌ Connect failed: ${e?.message || e}`);
    }
  };

  const refreshScenarioList = async () => {
    if (!scenarioPanelEnabled) return;
    if (!webSocketService.isConnected()) {
      console.warn('[Scenario] cannot refresh list: socket not connected');
      return;
    }
    setScenarioLoading(true);
    try {
      const ack: any = await webSocketService.emit('dev:scenario:list');
      if (Array.isArray(ack?.scenarios)) {
        setScenarioList(ack.scenarios);
        console.log(`[Scenario] list refreshed (${ack.scenarios.length})`);
      } else {
        console.warn('[Scenario] list response empty');
      }
    } catch (e:any) {
      console.error(`[Scenario] list error: ${e?.message || e}`);
    } finally {
      setScenarioLoading(false);
    }
  };

  const runScenario = async (name: string) => {
    if (!scenarioPanelEnabled || !name) return;
    try {
      const ack: any = await webSocketService.emit('dev:scenario:run', { name });
      if (ack?.success) {
        setScenarioStatus('queued');
        console.log(`[Scenario] run accepted: ${name}`);
      } else {
        console.warn(`[Scenario] run failed (${name}): ${ack?.error || 'unknown'}`);
      }
    } catch (e:any) {
      console.error(`[Scenario] run error (${name}): ${e?.message || e}`);
    }
  };

  const stopScenario = async () => {
    if (!scenarioPanelEnabled) return;
    try {
      const ack: any = await webSocketService.emit('dev:scenario:stop');
      if (ack?.success) {
        console.log('[Scenario] stop requested');
      } else {
        console.warn(`[Scenario] stop failed: ${ack?.error || 'no scenario running'}`);
      }
    } catch (e:any) {
      console.error(`[Scenario] stop error: ${e?.message || e}`);
    }
  };

  useEffect(() => {
    if (!scenarioPanelEnabled) return;
    const onStarted = (payload: any) => {
      const name = payload?.name || payload?.scenario || 'unknown';
      setActiveScenario(name);
      setScenarioStatus('running');
      console.log(`[Scenario] started: ${name}`);
    };
    const onCompleted = (payload: any) => {
      const name = payload?.name || 'unknown';
      setScenarioStatus('completed');
      setActiveScenario(null);
      console.log(`[Scenario] completed: ${name}`);
    };
    const onAborted = (payload: any) => {
      const name = payload?.name || 'unknown';
      const error = payload?.error;
      setScenarioStatus('aborted');
      setActiveScenario(null);
      console.warn(`[Scenario] aborted: ${name}${error ? ` (${error})` : ''}`);
    };
    try {
      webSocketService.onServerEvent('scenario:started', onStarted);
      webSocketService.onServerEvent('scenario:completed', onCompleted);
      webSocketService.onServerEvent('scenario:aborted', onAborted);
    } catch {}
    return () => {
      try {
        webSocketService.offServerEvent('scenario:started', onStarted);
        webSocketService.offServerEvent('scenario:completed', onCompleted);
        webSocketService.offServerEvent('scenario:aborted', onAborted);
      } catch {}
    };
  }, [scenarioPanelEnabled]);

  // 初始化测试引擎（使用test模式避免agent数量限制）
  const initTestEngine = async () => {
    try {
      await webSocketService.emit('test:engine:init', { width: 50, height: 50 });
      console.log('🧪 Test engine initialized');
    } catch (e: any) {
      console.error(`❌ Test engine init failed: ${e?.message || e}`);
    }
  };

  // 手动注册3个demo agents（使用test模式）
  const registerAgents = async () => {
    // 先初始化测试引擎
    await initTestEngine();

    const testAgents = [
      { id: 'demo_agent1', color: 'white', position: { x: 5, y: 5 } },
      { id: 'demo_agent2', color: 'purple', position: { x: 8, y: 5 } },
      { id: 'demo_agent3', color: 'orange', position: { x: 5, y: 8 } },
    ];

    for (const agent of testAgents) {
      const payload: any = {
        agentId: agent.id,
        position: agent.position,
        agentType: 'agent',
        color: agent.color,
        attributes: { health: 25, strength: 25, defense: 25, speed: 25 } // Total: 100
      };
      try {
        const ack: any = await webSocketService.emit('test:agent:register', payload);
        if (ack?.success) {
          console.log(`✅ Registered ${agent.id} (${agent.color}) → ok`);
        } else {
          console.error(`❌ Failed to register ${agent.id}:`);
          console.error('  Error:', ack?.error);
          console.error('  Errors array:', JSON.stringify(ack?.errors, null, 2));
          console.error('  Full response:', ack);
        }
      } catch (e) {
        console.error(`❌ Exception registering ${agent.id}:`, e);
      }
    }
  };

  // 生成测试资源（6个随机资源）
  const spawnTestResources = async () => {
    try {
      const weaponTypes = ['SWORD', 'AXE', 'LANCE'];
      const medicalTypes = ['large', 'small'];

      const items = [
        { type: 'WEAPON', position: { x: 7, y: 5 }, metadata: { weaponType: weaponTypes[0] } },
        { type: 'MEDICAL', position: { x: 6, y: 6 }, metadata: { medicalType: medicalTypes[0] } },
        { type: 'WEAPON', position: { x: 9, y: 6 }, metadata: { weaponType: weaponTypes[1] } },
        { type: 'MEDICAL', position: { x: 7, y: 8 }, metadata: { medicalType: medicalTypes[1] } },
        { type: 'WEAPON', position: { x: 4, y: 7 }, metadata: { weaponType: weaponTypes[2] } },
        { type: 'MEDICAL', position: { x: 6, y: 4 }, metadata: { medicalType: medicalTypes[0] } },
      ];

      const ack: any = await webSocketService.emit('test:resource:spawn', { items, currentTurn: 0 });
      if (ack?.success) {
        console.log(`✅ Spawned ${ack?.items?.length || 0} resources`);
      } else {
        console.error(`❌ Failed to spawn resources:`, ack?.error);
      }
    } catch (e: any) {
      console.error(`❌ Spawn resources error:`, e?.message || e);
    }
  };

  // Demo movement logic (连续单元格移动)
  const startDemoMovement = () => {
    console.log('🚶 Starting demo movement sequence...');

    const agent1Id = 'demo_agent1';
    // 生成连续移动路径：从(5,5)开始，绕一个小圈
    const moveSequence = [
      { x: 6, y: 5 }, { x: 7, y: 5 }, { x: 8, y: 5 },  // 向右
      { x: 8, y: 6 }, { x: 8, y: 7 }, { x: 8, y: 8 },  // 向下
      { x: 7, y: 8 }, { x: 6, y: 8 }, { x: 5, y: 8 },  // 向左
      { x: 5, y: 7 }, { x: 5, y: 6 }, { x: 5, y: 5 }   // 向上回到起点
    ];

    let currentStep = 0;
    let lastPosition = { x: 5, y: 5 };

    const performNextMove = () => {
      if (currentStep >= moveSequence.length) {
        console.log('✅ Demo movement completed');
        return;
      }

      const targetPos = moveSequence[currentStep];
      console.log(`🚶 Demo: Moving ${agent1Id} from (${lastPosition.x}, ${lastPosition.y}) to (${targetPos.x}, ${targetPos.y})...`);

      webSocketService.triggerGameEvent('AGENT_MOVED', {
        agentId: agent1Id,
        fromPosition: lastPosition,
        toPosition: targetPos,
        position: targetPos
      });

      lastPosition = targetPos;
      currentStep++;

      setTimeout(performNextMove, 800); // 稍快一些，800ms一步
    };

    setTimeout(performNextMove, 500);
  };

  return (
    <div className="game-container">
      {/* Phaser3 全屏游戏画布 */}
      <div id="phaser-game" ref={gameCanvasRef} />

      {/* 🏆 游戏结束赢家提示 */}
      <WinnerBanner />

      {scenarioPanelEnabled && (
        <div style={{ position:'absolute', right:16, top:90, width:300, background:'rgba(8,10,20,0.9)', border:'1px solid rgba(14,165,233,0.4)', borderRadius:8, padding:12, color:'#e2e8f0', fontSize:12, zIndex:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <div style={{ fontWeight:600, fontSize:13 }}>Fake Backend Scenarios</div>
            <span style={{ color:'#38bdf8' }}>{scenarioRunningLabel}</span>
          </div>
          <div style={{ display:'grid', gap:6, gridTemplateColumns:'repeat(2, minmax(0, 1fr))', marginBottom:8 }}>
            {scenarioChoices.map((sc) => (
              <button
                key={sc.name}
                onClick={() => runScenario(sc.name)}
                disabled={scenarioButtonsDisabled}
                title={sc.description || sc.name}
                style={{ padding:'6px 8px', fontSize:11, borderRadius:4, border:'1px solid rgba(59,130,246,0.4)', background: scenarioButtonsDisabled ? 'rgba(30,41,59,0.6)' : 'rgba(30,64,175,0.8)', color:'#e0f2fe', cursor: scenarioButtonsDisabled ? 'not-allowed' : 'pointer', opacity: scenarioButtonsDisabled ? 0.6 : 1 }}
              >
                {sc.name}
              </button>
            ))}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={refreshScenarioList} disabled={scenarioLoading || !connected} style={{ flex:1, padding:'6px 8px', fontSize:11, borderRadius:4, border:'1px solid rgba(59,130,246,0.4)', background: 'rgba(15,23,42,0.9)', color:'#e0f2fe', opacity: (!connected)?0.6:1 }}>
              {scenarioLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button onClick={stopScenario} disabled={!activeScenario} style={{ flex:1, padding:'6px 8px', fontSize:11, borderRadius:4, border:'1px solid rgba(248,113,113,0.4)', background:'rgba(127,29,29,0.8)', color:'#fee2e2', opacity: (!activeScenario)?0.6:1 }}>
              Stop
            </button>
          </div>
        </div>
      )}

      {/* 地图加载指示器移除：统一由全局 GlobalLoadingOverlay 控制 */}

      {/* 顶部状态栏 */}
      <TopBar
        data={{
          currentRound,
          aliveCount: panelAgents.filter(a => a.status !== 'died').length,
          totalCount: Math.max(panelAgents.length, 10),
          userBalance
        }}
      />

      {/* 左上角3个图标 */}
      <div className="left-top-icons">
        <div className="icon-btn" onClick={() => {
          setShowChat(!showChat);
          setShowPlayerLeaderboard(false);
          setShowBettingLeaderboard(false);
        }}>
          💬
        </div>
        <div className="icon-btn" onClick={() => {
          setShowPlayerLeaderboard(!showPlayerLeaderboard);
          setShowChat(false);
          setShowBettingLeaderboard(false);
        }}>
          🏆
        </div>
        <div className="icon-btn" onClick={() => {
          setShowBettingLeaderboard(!showBettingLeaderboard);
          setShowChat(false);
          setShowPlayerLeaderboard(false);
        }}>
          💰
        </div>
        {/* 🎥 相机自动跟随开关 */}
        <div
          className="icon-btn"
          title={autoFollow ? 'Auto Follow: ON' : 'Auto Follow: OFF'}
          onClick={() => {
            const next = !autoFollow;
            setAutoFollow(next);
            try { webSocketService.updateGameState('autoFollowCamera', next); } catch {}
          }}
          style={{ opacity: autoFollow ? 1 : 0.5 }}
        >
          🎥
        </div>
      </div>

      {/* 3个可展开的面板 */}
      <LiveChatPanel isVisible={showChat} onClose={() => setShowChat(false)} />
      <PlayerLeaderboard isVisible={showPlayerLeaderboard} onClose={() => setShowPlayerLeaderboard(false)} />
      <BettingLeaderboard isVisible={showBettingLeaderboard} onClose={() => setShowBettingLeaderboard(false)} />

      {/* 右侧Agent面板 */}
      <AgentPanel
        agents={panelAgents}
        onBet={handleBet}
        userBalance={userBalance}
        portraits={portraits}
      />

      {/* 底部活动滚动栏 */}
      <ActivityLogBar logs={activityLogs} />
    </div>
  );
};
